///
/// EarthBrowser
///
/// Created by Matt Giger on 10/28/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Native sign-in for the Capacitor apps (documentation/mobile/NATIVE_AUTH_PLAN.md).
/// The OS presents the credential UI - the Sign in with Apple sheet, the
/// Google account picker, the passkey sheet - and hands back an identity
/// token; the webview posts it to api-server, which verifies it and sets
/// the session cookies on that response. Passkeys need no code here: the
/// @capgo/capacitor-passkey shim routes navigator.credentials.* to the OS,
/// so utils/passkey.ts runs unchanged.
///

import { SocialLogin } from '@capgo/capacitor-social-login'
import { CapacitorPasskey } from '@capgo/capacitor-passkey'
import { setAuthOpen, setDeviceLimitToken, setWelcomeOpen } from '@/redux/dialogSlice'
import { appStore } from '@/redux/store'
import { userApi } from '@/redux/userApi'
import { getDeviceId } from '@/utils/deviceId'
import { logger } from '@/utils/LogDispatcher'
import { nativeRequestHeaders } from './device'
import { isNativeApp, nativePlatform } from './platform'

export type NativeOAuthProvider = 'apple' | 'google'

export type NativeSigninResult =
	| { status: 'ok'; registered: boolean }
	| { status: 'cancelled' }
	| { status: 'deviceLimit'; evictionToken: string }
	| { status: 'error'; message: string }

const cWebClientId = import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID ?? ''
const cIosClientId = import.meta.env.VITE_GOOGLE_IOS_CLIENT_ID ?? ''
const cAppleAppId = 'com.earthbrowser.app'

let initialized: Promise<void> | undefined

/** Wire the native providers and the passkey shim; idempotent, no-op on the web */
export function initNativeAuth(): Promise<void> {
	if (!isNativeApp()) return Promise.resolve()
	initialized ??= (async () => {
		try {
			// Online mode: we only need the ID token, which api-server verifies
			// against the iOS client id directly. iOSServerClientId is left
			// unset on purpose - the iOS client lives in a different Google
			// Cloud project than the web client and the SDK refuses a
			// cross-project server id (Google's `sub` is global, so accounts
			// still match across web and app)
			await SocialLogin.initialize({
				apple: { clientId: cAppleAppId },
				google: {
					webClientId: cWebClientId || undefined,
					iOSClientId: cIosClientId || undefined,
					mode: 'online',
				},
			})
		} catch (error) {
			logger.error('[NativeAuth] SocialLogin.initialize failed', error)
		}
		try {
			await CapacitorPasskey.autoShimWebAuthn()
		} catch (error) {
			logger.error('[NativeAuth] passkey shim failed', error)
		}
	})()
	return initialized
}

/** Whether Google sign-in can run on this platform with the configured client ids */
export function isGoogleSigninConfigured(): boolean {
	const platform = nativePlatform()
	if (platform === 'ios') return cIosClientId.length > 0
	if (platform === 'android') return cWebClientId.length > 0
	return false
}

/** Native Sign in with Apple sheet → POST /api/auth/native/apple */
export async function signInWithApple(): Promise<NativeSigninResult> {
	await initNativeAuth()
	let login
	try {
		login = await SocialLogin.login({ provider: 'apple', options: { scopes: ['email', 'name'] } })
	} catch (error) {
		return classifyProviderError(error, 'Apple')
	}
	const result = login.result
	if (!result.idToken) {
		return { status: 'error', message: 'Apple did not return an identity token' }
	}
	return postCredential('/api/auth/native/apple', {
		identityToken: result.idToken,
		givenName: result.profile?.givenName ?? null,
		familyName: result.profile?.familyName ?? null,
		deviceFingerprint: getDeviceId(),
	})
}

/** Native Google account picker → POST /api/auth/native/google */
export async function signInWithGoogle(): Promise<NativeSigninResult> {
	await initNativeAuth()
	let login
	try {
		login = await SocialLogin.login({ provider: 'google', options: { scopes: ['email', 'profile'] } })
	} catch (error) {
		return classifyProviderError(error, 'Google')
	}
	const result = login.result
	if (result.responseType !== 'online' || !result.idToken) {
		return { status: 'error', message: 'Google did not return an ID token' }
	}
	return postCredential('/api/auth/native/google', {
		idToken: result.idToken,
		deviceFingerprint: getDeviceId(),
	})
}

/**
 * Apply a successful sign-in to the app: close the sheet, load the profile
 * (which also re-mints tile tokens, identifies analytics, etc.), and open
 * the welcome dialog for brand-new accounts.
 */
export function applyNativeSignin(result: NativeSigninResult): void {
	if (result.status === 'ok') {
		appStore.dispatch(setAuthOpen(false))
		void appStore.dispatch(userApi.endpoints.getUserProfile.initiate(undefined, { forceRefetch: true }))
		if (result.registered) {
			appStore.dispatch(setWelcomeOpen(true))
		}
	} else if (result.status === 'deviceLimit') {
		appStore.dispatch(setAuthOpen(false))
		appStore.dispatch(setDeviceLimitToken(result.evictionToken))
	}
}

async function postCredential(path: string, body: Record<string, unknown>): Promise<NativeSigninResult> {
	try {
		const response = await fetch(`${import.meta.env.VITE_API_URL}${path}`, {
			method: 'POST',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
				'X-Device-Id': getDeviceId(),
				...nativeRequestHeaders(),
			},
			body: JSON.stringify(body),
		})
		const data = await response.json().catch(() => ({})) as { registered?: boolean; deviceLimit?: string; message?: string }
		if (response.status === 409 && data.deviceLimit) {
			return { status: 'deviceLimit', evictionToken: data.deviceLimit }
		}
		if (!response.ok) {
			return { status: 'error', message: data.message ?? `Sign in failed (${response.status})` }
		}
		return { status: 'ok', registered: data.registered === true }
	} catch (error) {
		logger.error('[NativeAuth] credential post failed', error)
		return { status: 'error', message: 'Could not reach the sign-in service' }
	}
}

/** The user closing the system sheet is not an error worth a toast */
function classifyProviderError(error: unknown, provider: string): NativeSigninResult {
	// Capacitor plugin rejections are Error-like objects with a message
	const message = error instanceof Error
		? error.message
		: typeof (error as { message?: unknown })?.message === 'string' ? (error as { message: string }).message : ''
	const lower = message.toLowerCase()
	if (lower.includes('cancel') || lower.includes('1001') || lower.includes('user closed') || lower.includes('dismiss')) {
		return { status: 'cancelled' }
	}
	logger.error(`[NativeAuth] ${provider} login failed`, error)
	return { status: 'error', message: message || `${provider} sign-in failed` }
}
